<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { computed, ref } from "vue"
import { UiButton, UiCepInput, UiCnpjInput, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type AddressPayload = {
  id?: number | null
  label: "fiscal" | "fisico"
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  zip_code: string
  _destroy?: boolean
}

type PostalCodeAddress = {
  zip_code: string
  street: string
  district: string
  city: string
  state: string
}

type CompanyPayload = {
  id?: number | null
  name: string
  trade_name: string
  cnpj: string
  email: string
  phone: string
  website: string
  state_registration: string
  municipal_registration: string
  logo_url?: string | null
  addresses: AddressPayload[]
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  company: CompanyPayload
  errors?: Record<string, string[]>
}>()

const logoPreviewUrl = ref(props.company.logo_url || "")

const form = useForm({
  company: {
    name: props.company.name,
    trade_name: props.company.trade_name,
    cnpj: props.company.cnpj,
    email: props.company.email,
    phone: props.company.phone,
    website: props.company.website,
    state_registration: props.company.state_registration,
    municipal_registration: props.company.municipal_registration,
    logo: null as File | null,
    addresses_attributes: props.company.addresses.map((address) => ({ ...address, _destroy: false })),
  },
})

const notify = useNotifications()
const fieldError = (field: string) => props.errors?.[field]?.[0]
const visibleAddresses = computed(() => form.company.addresses_attributes.filter((address) => !address._destroy))

function addressError(index: number, field: string) {
  return props.errors?.[`company_addresses.${index}.${field}`]?.[0] || props.errors?.[`addresses_attributes.${index}.${field}`]?.[0]
}

function addAddress() {
  form.company.addresses_attributes.push({
    id: null,
    label: "fiscal",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    zip_code: "",
    _destroy: false,
  })
}

function removeAddress(index: number) {
  const address = form.company.addresses_attributes[index]
  if (!address) return

  if (address.id) {
    address._destroy = true
    return
  }

  form.company.addresses_attributes.splice(index, 1)
}

function applyAddressLookup(index: number, payload: PostalCodeAddress) {
  const address = form.company.addresses_attributes[index]
  if (!address) return

  address.zip_code = payload.zip_code || address.zip_code
  address.street = payload.street || ""
  address.district = payload.district || ""
  address.city = payload.city || ""
  address.state = payload.state || ""
}

function onLookupError(message: string) {
  notify.warning(message)
}

function onLogoSelected(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0] || null
  form.company.logo = file

  if (!file) {
    logoPreviewUrl.value = props.company.logo_url || ""
    return
  }

  logoPreviewUrl.value = URL.createObjectURL(file)
}

function submit() {
  const opts = {
    forceFormData: true,
    onSuccess: () => notify.success(props.mode === "create" ? "Empresa criada com sucesso" : "Empresa atualizada com sucesso"),
  }
  if (props.mode === "create") {
    form.post("/companies", opts)
    return
  }

  form.put(`/companies/${props.company.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/companies" back-label="Voltar para empresas">
    <form class="space-y-5" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiTextInput id="company-name" v-model="form.company.name" label="Razao social" placeholder="Ex: Fabrik Industrial" :error="fieldError('name')" />
        <UiTextInput id="company-trade-name" v-model="form.company.trade_name" label="Nome fantasia" :error="fieldError('trade_name')" />
        <UiCnpjInput id="company-cnpj" v-model="form.company.cnpj" label="CNPJ" :error="fieldError('cnpj')" />
        <UiTextInput id="company-email" v-model="form.company.email" type="email" label="Email" :error="fieldError('email')" />
        <UiTextInput id="company-phone" v-model="form.company.phone" label="Telefone" :error="fieldError('phone')" />
        <UiTextInput id="company-website" v-model="form.company.website" label="Site" placeholder="https://..." :error="fieldError('website')" />
        <UiTextInput id="company-state-registration" v-model="form.company.state_registration" label="Inscricao estadual" :error="fieldError('state_registration')" />
        <UiTextInput id="company-municipal-registration" v-model="form.company.municipal_registration" label="Inscricao municipal" :error="fieldError('municipal_registration')" />
      </div>

      <section class="rounded-md border border-slate-200 p-4">
        <h2 class="text-sm font-semibold text-slate-800">Logo da empresa</h2>
        <p class="mt-1 text-xs text-slate-500">Formatos aceitos: PNG, JPG, JPEG e WEBP (maximo de 2MB).</p>

        <div class="mt-3 flex flex-wrap items-center gap-4">
          <div class="h-20 w-20 overflow-hidden rounded border border-slate-200 bg-slate-50">
            <img v-if="logoPreviewUrl" :src="logoPreviewUrl" alt="Preview da logo" class="h-full w-full object-contain" />
            <div v-else class="flex h-full items-center justify-center text-xs text-slate-400">Sem logo</div>
          </div>

          <input id="company-logo" type="file" accept="image/png,image/jpeg,image/webp" @change="onLogoSelected" />
        </div>
        <p v-if="fieldError('logo')" class="mt-2 text-sm text-rose-600">{{ fieldError('logo') }}</p>
      </section>

      <section class="rounded-md border border-slate-200 p-4">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-slate-800">Enderecos da empresa</h2>
          <UiButton type="button" size="sm" variant="secondary" @click="addAddress">Adicionar endereco</UiButton>
        </div>

        <div class="space-y-3">
          <article
            v-for="(address, index) in form.company.addresses_attributes"
            v-show="!address._destroy"
            :key="index"
            class="rounded-md border border-slate-100 p-3"
          >
            <div class="grid gap-3 md:grid-cols-4">
              <UiSelect
                :id="`company-address-label-${index}`"
                :model-value="address.label"
                label="Tipo"
                :options="[
                  { label: 'Fiscal', value: 'fiscal' },
                  { label: 'Fisico', value: 'fisico' },
                ]"
                :error="addressError(index, 'label')"
                @update:model-value="address.label = String($event || 'fiscal') as 'fiscal' | 'fisico'"
              />
              <UiCepInput
                :id="`company-address-zip-${index}`"
                v-model="address.zip_code"
                label="CEP"
                helper-text="Informe o CEP para buscar endereco automaticamente"
                :error="addressError(index, 'zip_code')"
                @lookup-success="applyAddressLookup(index, $event)"
                @lookup-error="onLookupError"
              />
              <UiTextInput :id="`company-address-street-${index}`" v-model="address.street" label="Rua" :error="addressError(index, 'street')" />
              <UiTextInput :id="`company-address-number-${index}`" v-model="address.number" label="Numero" :error="addressError(index, 'number')" />
            </div>

            <div class="mt-3 grid gap-3 md:grid-cols-4">
              <UiTextInput :id="`company-address-complement-${index}`" v-model="address.complement" label="Complemento" :error="addressError(index, 'complement')" />
              <UiTextInput :id="`company-address-district-${index}`" v-model="address.district" label="Bairro" :error="addressError(index, 'district')" />
              <UiTextInput :id="`company-address-city-${index}`" v-model="address.city" label="Cidade" :error="addressError(index, 'city')" />
              <UiTextInput :id="`company-address-state-${index}`" v-model="address.state" label="UF" :error="addressError(index, 'state')" />
            </div>

            <div class="mt-3 flex justify-end">
              <UiButton type="button" size="sm" variant="danger" @click="removeAddress(index)">Remover endereco</UiButton>
            </div>
          </article>

          <p v-if="visibleAddresses.length === 0" class="text-sm text-slate-500">Nenhum endereco informado.</p>
        </div>
      </section>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar empresa" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/companies" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>
